import clsx from 'clsx';
import React, { useState, useEffect, useCallback, useMemo, useRef, ReactNode } from 'react';
import { MdExpandMore as ExpandMore } from 'react-icons/md';
import Select, { components, createFilter, ActionMeta, GroupBase, OnChangeValue, FilterOptionOption, StylesConfig, Props as SelectProps } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import type { SelectOption } from '../types/common';

import VirtualizedMenuList from './components/VirtualizedMenuList';
import useClassNamesCompat, { ClassNamesConfigCompat } from './hooks/useClassNamesCompat';

import './BaySelect.scss';

// React Select uses @emotion styled components
// under the hood for style overrides
const styles: Partial<StylesConfig<SelectOption, boolean>> = {
	container: (base) => ({
		...base,
		flex: 1,
	}),
	input: (base) => ({
		...base,
		width: 'auto',
		minWidth: 5,
	}),
};

const defaultAllOption: SelectOption = {
	label: 'All',
	value: 'all',
};

export interface BaySelectProps extends Omit<SelectProps<SelectOption, boolean, GroupBase<SelectOption>>, 'onChange' | 'value' | 'defaultValue' | 'id' | 'classNames'> {
	onChangeFunc: (value: OnChangeValue<SelectOption, boolean>, meta: ActionMeta<SelectOption>) => void;
	classNames?: ClassNamesConfigCompat;
	optionsArray: SelectOption[];
	flex?: boolean;
	id?: string;
	name?: string;
	className?: string;
	defaultValue?: SelectOption | SelectOption[] | null;
	controlledValue?: SelectOption | SelectOption[] | null;
	onCreateOptionFunc?: (inputValue: string) => void;
	isOptionDisabled?: (option: SelectOption) => boolean;
	placeholder?: string;
	disabled?: boolean;
	isClearable?: boolean;
	isMulti?: boolean;
	noTags?: boolean;
	hideSelectedOne?: boolean;
	customStyles?: Partial<StylesConfig<SelectOption, boolean>>;
	allowSearch?: boolean;
	openMenuOnFocus?: boolean;
	addMultiSelectValues?: SelectOption[];
	customTemplate?: ((data: SelectOption, isSelected: boolean) => ReactNode) | null;
	customSingleValueTemplate?: ((data: SelectOption) => ReactNode) | null;
	allowSelectAll?: boolean;
	createable?: boolean;
	menuShouldScrollIntoView?: boolean;
	isParentLoading?: boolean;
	hasDivider?: boolean;
	noOptionMessage?: boolean;
	NoOptionsMessage?: React.ComponentType;
	isOptionGroup?: boolean;
	customFilter?: FilterOptionOption<SelectOption> | null;
	useVirtualScroll?: boolean;
	optionHeight?: number;
	/**
	 * Caps the virtualised menu at N option rows so a long list scrolls instead of
	 * growing past the viewport. Omit to size the menu to the full option count.
	 */
	maxVisibleOptions?: number;
	allOption?: SelectOption;
}

const BaySelect: React.FC<BaySelectProps> = (props) => {
	const [selectedValue, setSelectedValue] = useState<SelectOption | null>(null);
	const [selectedOptions, setSelectedOptions] = useState<SelectOption[]>([]);
	const [isSelectedAll, setIsSelectedAll] = useState(false);

	const {
		onChangeFunc,
		optionsArray,
		flex,
		id,
		name,
		className,
		defaultValue,
		controlledValue,
		onCreateOptionFunc,
		isOptionDisabled,
		placeholder = 'Select...',
		disabled = false,
		isClearable = true,
		isMulti = false,
		noTags = false,
		hideSelectedOne = true,
		customStyles = {},
		allowSearch = true,
		openMenuOnFocus = false,
		addMultiSelectValues = [],
		customTemplate = null,
		customSingleValueTemplate = null,
		allowSelectAll = true,
		createable = false,
		menuShouldScrollIntoView = true,
		isParentLoading = false,
		hasDivider = false,
		noOptionMessage = false,
		NoOptionsMessage,
		isOptionGroup = false,
		customFilter = null,
		useVirtualScroll = false,
		optionHeight = 40,
		maxVisibleOptions,
		allOption = defaultAllOption,
		...restProps
	} = props;

	// Handle change for select
	const handleChange = (e: OnChangeValue<SelectOption, boolean>, meta: ActionMeta<SelectOption>) => {
		setSelectedValue(e as SelectOption);
		onChangeFunc(e, meta);
	};

	// Set out initial value if any
	useEffect(() => {
		// Can be null or undefined
		const providedValue = props.defaultValue || props.controlledValue;
		// If not a multi select
		// Single select expects an object
		if (!isMulti) {
			setSelectedValue(providedValue as SelectOption || null);
		} else {
			// If is a multi select
			// Multi select expects an array
			if (Array.isArray(providedValue) && providedValue?.length > 0) {
				// Option is set to 'all'
				if (providedValue?.map((a) => a?.value)?.includes('all')) {
					setSelectedOptions([allOption]);
					setIsSelectedAll(true);
					return;
				}
			}

			setSelectedOptions((providedValue as SelectOption[]) || []);
		}
	}, [props.defaultValue, props.controlledValue, isMulti, allOption]);

	// Add additional values to multi select
	useEffect(() => {
		if (addMultiSelectValues?.length && isMulti) {
			setSelectedOptions(addMultiSelectValues);
		}
	}, [addMultiSelectValues, isMulti]);

	//OnChange for multi select
	function onChangeMulti(value: OnChangeValue<SelectOption, true>, event: ActionMeta<SelectOption>) {
		if (isSelectedAll) {
			if (
				event.action === 'clear' ||
				event?.action === 'remove-value' ||
				(event?.action === 'deselect-option' && event?.option?.label === 'All')
			) {
				setIsSelectedAll(false);
				setSelectedOptions([]);
				onChangeFunc(value, event);
			}
			return;
		}
		// Deselect all the options
		if (
			event.action === 'clear' ||
			(event.action === 'deselect-option' && (event as any).option.value === 'all')
		) {
			setSelectedOptions([]);
		} else if (
			event.action === 'select-option' &&
			(event as any).option.value === 'all'
		) {
			// Select all the options
			setIsSelectedAll(true);
			setSelectedOptions([allOption]);
		} else if (event.action === 'deselect-option') {
			// Deselect single option
			const result = selectedOptions.filter(
				(o) => o.value !== 'all' && o.value !== (event as any).option.value,
			);
			setSelectedOptions(result);
		} else {
			setSelectedOptions(value as SelectOption[]);
		}
		onChangeFunc(value, event);
	}

	// Style classnames
	const cxClassNames = clsx([
		'bay-select-container',
		isMulti ? 'bay-multiselect' : null,
		className,
	]);

	// Re-routes a `classNames` config through each component's own `className`, for the
	// react-select versions that predate the prop -- see the note on the hook.
	const applyClassNames = useClassNamesCompat(props.classNames);

	// react-select renders every entry of its `components` map as a JSX *type*, so a
	// component function created during render is a new type on every render and React
	// unmounts and remounts the real DOM node -- taking the menu's scroll position and
	// the browser's hover/focus tracking with it. Reproducible by resizing the viewport
	// (or any parent re-render) while the menu is open. Every component this file hands
	// to react-select is therefore created once and reads whatever the current render
	// settled on through a ref, rather than closing over it.
	const latest = useRef({
		placeholder,
		noTags,
		selectedOptions,
		allOption,
		customTemplate,
		customSingleValueTemplate,
		NoOptionsMessage,
		optionHeight,
		maxVisibleOptions,
	});

	latest.current = {
		placeholder,
		noTags,
		selectedOptions,
		allOption,
		customTemplate,
		customSingleValueTemplate,
		NoOptionsMessage,
		optionHeight,
		maxVisibleOptions,
	};

	// Created once -- see the note above `latest`. Anything these read out of the
	// component's props comes from `latest.current`, never from this render's closure.
	const {
		Placeholder,
		DropdownIndicator,
		CustomMenuList,
		CustomOption,
		CustomSingleValue,
		OptionWithDivider,
		NoOptionsMessageSelect,
		GroupHeading,
	} = useMemo(() => ({
		// Placeholder component
		Placeholder: (props: any) => {
			const { placeholder, noTags, selectedOptions, allOption } = latest.current;
			let tempText = placeholder ?? null;

			if (noTags) {
				if (selectedOptions?.length > 0) {
					tempText = `(${selectedOptions?.length}) Selected`;
				}

				if (selectedOptions?.some((val) => val?.value === allOption?.value)) {
					tempText = 'All is selected';
				}
			}

			return (
				<components.Placeholder {...props}>{tempText}</components.Placeholder>
			);
		},

		DropdownIndicator: (props: any) => {
			return (
				<components.DropdownIndicator {...props}>
					<ExpandMore size={24} />
				</components.DropdownIndicator>
			);
		},

		// Only the plumbing lives here -- the sizing rules are in VirtualizedMenuList.
		// `className` is what `applyClassNames` wrapping this slot delivers, so it has
		// to reach the element the list renders rather than being dropped.
		CustomMenuList: (props: any) => {
			const { maxVisibleOptions, optionHeight } = latest.current;

			return (
				<VirtualizedMenuList
					className={props.className}
					header={props.selectProps.menuListHeader}
					maxHeight={props.maxHeight}
					maxVisibleOptions={maxVisibleOptions}
					optionHeight={optionHeight}
					style={props.getStyles('menuList', props)}
				>
					{props.children}
				</VirtualizedMenuList>
			);
		},

		// Custom Option component. The underscored names are destructured only to be
		// dropped -- dropping the two mouse handlers is the point of this component.
		// NOTE: https://github.com/JedWatson/react-select/issues/2711
		// NOTE: https://github.com/JedWatson/react-select/issues/3128
		CustomOption: ({ children: _children, ...props }: any) => {
			const { customTemplate } = latest.current;
			const { onMouseMove: _onMouseMove, onMouseOver: _onMouseOver, ...rest } = props.innerProps;
			const newProps = Object.assign(props, { innerProps: rest });

			return (
				<components.Option {...newProps}>
					{customTemplate ? customTemplate(props.data, props.isSelected) : null}
				</components.Option>
			);
		},

		CustomSingleValue: (props: any) => {
			const { customSingleValueTemplate } = latest.current;

			return (
				<components.SingleValue {...props}>
					{customSingleValueTemplate
						? customSingleValueTemplate(props.data)
						: props.children}
				</components.SingleValue>
			);
		},

		OptionWithDivider: (props: any) => {
			return (
				<div>
					<components.Option {...props} />
					{props.data?.divider && <div className="border-bottom my-1 mx-2" />}
				</div>
			);
		},

		NoOptionsMessageSelect: (props: any) => {
			const { NoOptionsMessage } = latest.current;

			return (
				<components.NoOptionsMessage {...props}>
					{NoOptionsMessage && <NoOptionsMessage />}
				</components.NoOptionsMessage>
			);
		},

		GroupHeading: (props: any) => {
			// display the group divider only before the 2nd group onwards, not before the first group.
			const isFirstGroup = props?.selectProps?.options?.[0] === props?.data;

			return (
				<div>
					<components.GroupHeading {...props}>
						<div className="group-heading">
							{!isFirstGroup && <hr className="group-divider" />}
							<div className="fw-bold">{props.data.label}</div>
						</div>
					</components.GroupHeading>
				</div>
			);
		},
	}), []);

	// Construct Option components
	const getOptionComponents = () => {
		let components: Record<string, any> = {};

		components = { DropdownIndicator };

		if (noOptionMessage) {
			components = { ...components, NoOptionsMessage: NoOptionsMessageSelect };
		}

		if (customTemplate) {
			components = { ...components, Option: CustomOption };
		}

		if (customSingleValueTemplate) {
			components = { ...components, SingleValue: CustomSingleValue };
		}

		if (hasDivider) {
			components = { ...components, Option: OptionWithDivider };
		}

		if (isOptionGroup) {
			components = { ...components, GroupHeading };
		}

		if (useVirtualScroll) {
			components = { ...components, MenuList: CustomMenuList };
		}

		// Last, so it wraps whichever Option the branches above settled on rather
		// than being overwritten by them.
		return applyClassNames(components);
	};

	// Construct Multi Option components
	const getMultiOptionComponents = () => {
		let components: Record<string, any> = { DropdownIndicator, Placeholder };

		if (customTemplate) {
			components = { ...components, Option: CustomOption };
		}

		if (hasDivider) {
			components = { ...components, Option: OptionWithDivider };
		}

		if (noOptionMessage) {
			components = { ...components, NoOptionsMessage: NoOptionsMessageSelect };
		}

		if (isOptionGroup) {
			components = { ...components, GroupHeading };
		}

		return applyClassNames(components);
	};

	// Handle option chosen
	const onHandleOptionsChosen = useCallback(
		(option: SelectOption) => {
			// Is select 'all' option
			if (isSelectedAll) {
				return isSelectedAll;
			} else {
				// Is select option
				return selectedOptions?.map((a) => a?.value)?.includes(option?.value);
			}
		},
		[isSelectedAll, selectedOptions],
	);

	return !isMulti ? (
		<Select<SelectOption, false, GroupBase<SelectOption>>
			captureMenuScroll={useVirtualScroll}
			className={cxClassNames}
			classNamePrefix="bay-select"
			closeMenuOnSelect
			components={getOptionComponents() as any}
			defaultValue={defaultValue as SelectOption}
			filterOption={customFilter as any || createFilter({ ignoreAccents: false })}
			hideSelectedOptions={hideSelectedOne}
			id={String(id)}
			isClearable={isClearable}
			isDisabled={disabled}
			isLoading={isParentLoading}
			isOptionDisabled={isOptionDisabled}
			isSearchable={allowSearch}
			menuShouldScrollIntoView={menuShouldScrollIntoView}
			name={name}
			openMenuOnFocus={openMenuOnFocus}
			options={optionsArray}
			placeholder={placeholder}
			styles={{ ...(flex ? styles : {}), ...customStyles }}
			value={controlledValue ? (controlledValue as SelectOption) : selectedValue}
			onChange={(e, meta) => handleChange(e, meta)}
			{...restProps}
		/>
	) : createable ? (
		<CreatableSelect<SelectOption, true, GroupBase<SelectOption>>
			isSearchable
			className={cxClassNames}
			classNamePrefix="bay-select"
			closeMenuOnSelect={false}
			components={getMultiOptionComponents() as any}
			controlShouldRenderValue={!noTags}
			defaultValue={defaultValue as SelectOption[]}
			isDisabled={disabled}
			filterOption={customFilter as any || createFilter({ ignoreAccents: false })} // To improve performance on filtering
			hideSelectedOptions={false}
			id={String(id)}
			isClearable={isClearable}
			isLoading={isParentLoading}
			isMulti
			isOptionDisabled={isOptionDisabled}
			isOptionSelected={(option) => onHandleOptionsChosen(option)}
			name={name}
			options={allowSelectAll ? [allOption, ...optionsArray] : optionsArray}
			placeholder={placeholder}
			styles={{ ...(flex ? styles : {}), ...customStyles }}
			tabSelectsValue={false}
			value={controlledValue ? (controlledValue as SelectOption[]) : selectedOptions}
			onChange={onChangeMulti}
			onCreateOption={(inputValue) => onCreateOptionFunc && onCreateOptionFunc(inputValue)}
			{...restProps}
		/>
	) : (
		<Select<SelectOption, true, GroupBase<SelectOption>>
			isSearchable
			className={cxClassNames}
			classNamePrefix="bay-select"
			closeMenuOnSelect={false}
			components={getMultiOptionComponents() as any}
			controlShouldRenderValue={!noTags}
			defaultValue={defaultValue as SelectOption[]}
			filterOption={customFilter as any || createFilter({ ignoreAccents: false })} // To improve performance on filtering
			hideSelectedOptions={false}
			id={String(id)}
			isClearable={isClearable}
			isDisabled={disabled}
			isLoading={isParentLoading}
			isMulti
			isOptionDisabled={isOptionDisabled}
			isOptionSelected={(option) => onHandleOptionsChosen(option)}
			name={name}
			options={allowSelectAll ? [allOption, ...optionsArray] : optionsArray}
			placeholder={placeholder}
			styles={{ ...(flex ? styles : {}), ...customStyles }}
			tabSelectsValue={false}
			value={controlledValue ? (controlledValue as SelectOption[]) : selectedOptions}
			onChange={onChangeMulti}
			{...restProps}
		/>
	);
};

export default BaySelect;
